import React, { createContext, useContext, useEffect, useMemo } from 'react'

import { PageSource } from 'types/graphql'

import { serializePageContext } from './serialize'
import { PageContextEnvelope } from './types'

type PageContextValue = {
  envelope: PageContextEnvelope
  serialized: string
}

const PageContext = createContext<PageContextValue | null>(null)

// Module-level store so components outside the PageContextProvider tree
// (e.g. the AI assistant overlay, which renders as a sibling) can read
// the current page context.
let globalPageContext: PageContextValue | null = null

export const getPageContext = (): PageContextValue | null => globalPageContext

/**
 * Maps the current page context to the GraphQL PageSource enum value.
 * Citizens see "ApplyForm"; staff see "RecordTemplateApplyForm" because
 * the backend routes them to different prompt pipelines.
 */
export const getPageSource = (viewer: 'citizen' | 'staff'): PageSource | undefined => {
  const pageType = globalPageContext?.envelope.page.pageType
  if (pageType === 'applyForm') {
    return viewer === 'citizen' ? 'ApplyForm' : 'RecordTemplateApplyForm'
  }
  if (pageType === 'recordDetail') {
    return 'RecordDetail'
  }
  return undefined
}

type Props = {
  envelope: PageContextEnvelope
  children: React.ReactNode
}

const PageContextProvider = ({ envelope, children }: Props) => {
  const value = useMemo<PageContextValue>(
    () => ({
      envelope,
      serialized: serializePageContext(envelope),
    }),
    [envelope]
  )

  useEffect(() => {
    globalPageContext = value
    return () => {
      if (globalPageContext === value) {
        globalPageContext = null
      }
    }
  }, [value])

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>
}

/**
 * For components that render inside the PageContextProvider tree.
 * Components outside the tree (e.g. AI assistant overlays) should
 * use {@link getPageContext} instead.
 */
export const usePageContext = (): PageContextValue | null => {
  return useContext(PageContext)
}

export default React.memo(PageContextProvider)
